import { BaseQLClient } from '@adornis/baseql/client/baseqlClient.js';
import { provideGlobalContext } from '@adornis/functional-lit/directive-provide-context.js';
import { initApp } from '@adornis/functional-lit/init-app.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { translationContext } from '@adornis/internationalization/client/translation-context.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { functionalLogin } from '@adornis/user-forms/function-login.js';
import { subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import { initSaraaAbac } from '@saraa/permissions/abac.js';
import { SaraaUser } from '@saraa/permissions/db/SaraaUser.js';
import { html } from 'lit';
import { initSaraaErm } from '../erm.js';
import { m } from '../localization/out/messages.js';
import { GeneratePage } from './generate-page.js';
import { initializeErm } from './initalizeErm.js';
import { initLocale } from './initLocale.js';
import { NotFoundPage } from './not-found-page.js';

import './tailwind.css' with { type: 'tailwind' };
import '../index.js';

/**
 * Locale first: the adornis packages resolve theirs lazily, on the first message they
 * render, so this only has to happen before anything is rendered — but it has to happen,
 * or they each fall back to their own base locale. See `initLocale`.
 */
initLocale();

/**
 * Baseql websocket and users init
 */
BaseQLClient.initDefault();
void subscribeCurrentUser();

/**
 * Authorization. `initSaraaAbac` takes no datalayer on the client — permissions
 * are resolved through baseql.
 */
initSaraaAbac();
initSaraaErm();

/**
 * ERM initialization
 */
const { ermRouter, renderERM } = initializeErm();

/**
 * Top-level router
 * `/`        → user-facing generate page
 * `/admin/*` → ERM admin app
 */
const router = makeRouter(
  {
    generate: { path: '/', render: () => GeneratePage() },
    admin: { path: '/admin{/*}?', subRouter: ermRouter, render: renderERM },
  },
  // Rendered when no route matches the current path (or a route fails to render).
  { fallback: () => NotFoundPage() },
).init();

// ── App ────────────────────────────────────────────────────────────────────
// The whole app sits behind the authentication guard, so a user is prompted to
// log in before reaching either the generate page or the admin area. The guard
// only re-renders on auth changes, so the routed content is its own reactive
// component to react to navigation.
const app = stateful(() => html`${router.outlet.value}`);

void initApp(() => {
  provideGlobalContext(translationContext, m);
  return html`
    ${authenticationGuard(
      () => app(),
      () =>
        html`${functionalLogin({
          // Only used to resolve the collection to search, which SaraaUser shares
          // with AdornisUser; the cast bridges the generic Permission parameter.
          userClass: SaraaUser as unknown as typeof AdornisUser,
          /**
           * No `onLogin` cache reset any more. Permissions used to be resolved
           * through GraphQL and cached per session for an hour, with nothing
           * invalidating it — so signing out and back in as a different user in
           * the same tab (the guard re-renders, it never reloads the page) handed
           * the new user the previous user's permissions, and a demotion took up
           * to an hour to apply. Grants are now read straight off the live
           * `currentUser()` subscription, so both cases resolve themselves.
           */
        })}`,
    )}
  `;
});
